import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', role: 'restaurant', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function setField(f, v) { setForm(prev => ({ ...prev, [f]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Could not send your message.');
      }
      setSent(true);
    } catch (err) {
      setError(err.message || 'Could not send your message. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-3xl font-black text-white mb-3" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>Message Sent!</h2>
          <p className="text-gray-400 mb-6">Thanks for reaching out. A Starfuel team member will respond within 2 hours during business hours.</p>
          <Link to="/" className="px-6 py-3 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <nav className="border-b border-gray-800 px-4 md:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="font-black text-2xl text-yellow-400" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>STARFUEL</Link>
        <div className="flex items-center gap-3">
          <Link to="/pricing" className="text-gray-400 hover:text-white text-sm transition">Pricing</Link>
          <Link to="/about" className="text-gray-400 hover:text-white text-sm transition">About</Link>
          <Link to="/faq" className="text-gray-400 hover:text-white text-sm transition">FAQ</Link>
          <Link to="/login" className="text-gray-400 hover:text-white text-sm transition">Sign In</Link>
          <Link to="/register" className="px-4 py-2 bg-yellow-400 text-black text-sm font-bold rounded-xl hover:bg-yellow-300 transition">Get Started</Link>
        </div>
      </nav>
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <div>
            <p className="text-yellow-400 font-mono text-xs tracking-widest uppercase mb-4">Contact Us</p>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-6" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>LET'S<br /><span className="text-yellow-400">TALK.</span></h1>
            <p className="text-gray-400 leading-relaxed mb-10">Whether you are a restaurant owner with questions, a driver ready to sign up, or a business looking to partner — we want to hear from you.</p>
            <div className="space-y-6">
              {[
                { icon: '✉️', label: 'Email', value: 'legal@starfuelcarfleet.com' },
                { icon: '🌐', label: 'Website', value: 'starfuelcarfleet.com' },
                { icon: '📍', label: 'Location', value: 'Tulsa, Oklahoma' },
                { icon: '🕐', label: 'Response Time', value: 'Within 2 hours (business hours)' },
              ].map(item => (
                <div key={item.label} className="flex items-start gap-4">
                  <span className="text-2xl">{item.icon}</span>
                  <div>
                    <p className="text-xs text-gray-500 font-mono uppercase tracking-wider">{item.label}</p>
                    <p className="text-white font-medium mt-0.5">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 p-5 bg-yellow-400/10 border border-yellow-400/30 rounded-2xl">
              <p className="text-yellow-400 font-semibold text-sm mb-2">Ready to get started?</p>
              <p className="text-gray-400 text-sm mb-4">Skip the form and sign up directly. It only takes 2 minutes.</p>
              <Link to="/register" className="inline-block px-5 py-2.5 bg-yellow-400 text-black font-bold text-sm rounded-xl hover:bg-yellow-300 transition">Create Free Account →</Link>
            </div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
            <h2 className="font-bold text-lg mb-6">Send us a message</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Your Name</label>
                  <input type="text" value={form.name} onChange={e => setField('name', e.target.value)} required className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition" placeholder="Maya Williams" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">Email</label>
                  <input type="email" value={form.email} onChange={e => setField('email', e.target.value)} required className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition" placeholder="you@business.com" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">I am a...</label>
                <div className="flex gap-2">
                  {['restaurant', 'driver', 'partner', 'other'].map(r => (
                    <button key={r} type="button" onClick={() => setField('role', r)} className={`flex-1 py-2 rounded-xl text-xs font-medium capitalize transition ${form.role === r ? 'bg-yellow-400 text-black' : 'bg-black border border-gray-700 text-gray-400 hover:border-gray-500'}`}>{r}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Subject</label>
                <input type="text" value={form.subject} onChange={e => setField('subject', e.target.value)} required className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition" placeholder="Question about pricing..." />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Message</label>
                <textarea value={form.message} onChange={e => setField('message', e.target.value)} required rows={5} className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition resize-none" placeholder="Tell us about your business and how we can help..." />
              </div>
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">{error}</div>
              )}
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">{error}</div>
              )}
              <button type="submit" disabled={loading} className="w-full py-3.5 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl transition-all active:scale-95 disabled:opacity-50">{loading ? 'Sending...' : 'Send Message →'}</button>
            </form>
          </div>
        </div>
      </main>
      <footer className="border-t border-gray-800 px-4 md:px-8 py-8 mt-8">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-yellow-400 font-black text-xl" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>STARFUEL</p>
          <p className="text-gray-600 text-sm">© 2026 Starfuel Enterprises, Inc. — Tulsa, Oklahoma</p>
          <div className="flex gap-4 text-sm text-gray-500"><Link to="/terms" className="hover:text-white transition">Terms</Link><Link to="/privacy" className="hover:text-white transition">Privacy</Link></div>
        </div>
      </footer>
    </div>
  );
}
